<template>
    <MainContent
        title="Descuentos"
        :value="filters"
        :meta="meta"
        @input="val => Object.assign(filters, val)"
    >
        <template #header>
            <Card customClasses="tw:mb-4">
                <div class="tw:flex tw:flex-col tw:gap-4 tw:w-full">
                    <div class="tw:flex tw:flex-col tw:sm:flex-row tw:items-center tw:gap-3 tw:justify-between">
                        <div class="tw:sm:w-1/2 tw:w-full">
                            <SearchComponent
                                v-model="filters.search"
                                placeholder="Buscar"
                            />
                        </div>
                        <div class="tw:flex tw:w-full tw:sm:w-auto tw:items-center tw:flex-col tw:sm:flex-row tw:gap-2 tw:sm:shrink-0">
                            <Button
                                icon="fas fa-plus"
                                @click="showModal('create-descuento')"
                                customClass="tw:text-xs tw:sm:text-sm tw:w-full tw:sm:w-auto"
                            >
                                Crear Descuento
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>
        </template>

        <LoaderComponent v-if="loading" />

        <div v-else-if="vouchers.length === 0" class="tw:text-center tw:py-12 tw:text-gray-400">
            <i class="fas fa-ticket-alt tw:text-4xl tw:mb-3 tw:block"></i>
            No hay descuentos registrados
        </div>

        <Card v-else>
            <b-table
                hover
                :items="vouchers"
                :fields="fields"
                responsive
            >
                <template #cell(name)="{ item }">
                    <span class="tw:font-medium tw:text-gray-800">{{ item.name }}</span>
                </template>

                <template #cell(code)="{ item }">
                    <code class="tw:bg-slate-100 tw:px-2 tw:py-0.5 tw:rounded tw:text-xs tw:font-mono">{{ item.code }}</code>
                </template>

                <template #cell(discount_percent)="{ item }">
                    <span v-if="item.discount_percent" class="tw:text-green-600 tw:font-medium">
                        {{ item.discount_percent }}%
                    </span>
                    <span v-else class="tw:text-gray-300">—</span>
                </template>

                <template #cell(discount_fixed)="{ item }">
                    <span v-if="item.discount_fixed" class="tw:text-green-600 tw:font-medium">
                        {{ item.discount_fixed }} €
                    </span>
                    <span v-else class="tw:text-gray-300">—</span>
                </template>

                <template #cell(opciones)="{ item }">
                    <div class="tw:flex tw:gap-2 tw:justify-end">
                        <Button
                            type="button"
                            variant="ghost"
                            customClass="tw:text-xs tw:px-2 tw:py-1"
                            @click="showModal('update-descuento-' + item.id)"
                        >
                            <i class="fas fa-edit"></i>
                        </Button>
                        <Button
                            type="button"
                            variant="danger"
                            customClass="tw:text-xs tw:px-2 tw:py-1"
                            @click="showModal('delete-descuento-' + item.id)"
                        >
                            <i class="fas fa-trash"></i>
                        </Button>
                    </div>
                </template>
            </b-table>
        </Card>

        <!-- Modales per-item -->
        <CreateEventVoucher
            v-if="eventoId"
            :evento-id="eventoId"
            @close="closeModalAndRefresh"
        />

        <UpdateEventVoucher
            v-for="item in vouchers"
            :key="'update-' + item.id"
            :evento-id="eventoId"
            :voucher="item"
            @close="closeModalAndRefresh"
        />

        <DeleteEventVoucher
            v-for="item in vouchers"
            :key="'delete-' + item.id"
            :evento-id="eventoId"
            :voucher="item"
            @close="closeModalAndRefresh"
        />
    </MainContent>
</template>

<script setup>
    /**
     * Nomenclatura: "Descuentos" es la etiqueta de UI cara al usuario;
     * "vouchers" es el concepto de dominio (API, composable, store).
     * Ambos términos son equivalentes en este contexto.
     */
    import { onMounted, onUnmounted, getCurrentInstance, watch } from 'vue';
    import debounce from 'lodash/debounce';

    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue';
    import LoaderComponent from '@/components/preloader/LoaderComponent';
    import CreateEventVoucher from '@/modules/admin/eventos/descuentos/modals/CreateEventVoucher.vue';
    import UpdateEventVoucher from '@/modules/admin/eventos/descuentos/modals/UpdateEventVoucher.vue';
    import DeleteEventVoucher from '@/modules/admin/eventos/descuentos/modals/DeleteEventVoucher.vue';
    import { useEventVouchers } from '@/composables/admin/events/vouchers/useEventVouchers';

    const props = defineProps({
        evento_id: {
            type: [String, Number],
            required: true,
        },
    });

    const instance = getCurrentInstance();
    const vm = instance?.proxy;

    const eventoId = props.evento_id;

    const {
        vouchers,
        loading,
        filters,
        meta,
        fetchVouchers,
    } = useEventVouchers(props.evento_id);

    const fields = [
        { key: 'name', label: 'Nombre', sortable: true, class: 'align-middle' },
        { key: 'code', label: 'Código', sortable: true, class: 'align-middle' },
        { key: 'units', label: 'Unidades', sortable: true, class: 'align-middle text-center' },
        { key: 'discount_percent', label: 'Dto. %', sortable: true, class: 'align-middle text-center' },
        { key: 'discount_fixed', label: 'Dto. fijo', sortable: true, class: 'align-middle text-center' },
        { key: 'opciones', label: '', class: 'align-middle' },
    ];

    function showModal(modalName) {
        if (vm?.$modal) {
            vm.$modal.show(modalName);
        }
    }

    function closeModalAndRefresh(modalName) {
        if (vm?.$modal) {
            vm.$modal.hide(modalName);
        }
        fetchVouchers();
    }

    const fetchVouchersDebounced = debounce(() => {
        fetchVouchers();
    }, 500);

    onMounted(() => {
        fetchVouchers();
    });

    onUnmounted(() => {
        fetchVouchersDebounced.cancel();
    });

    watch(filters, () => {
        fetchVouchersDebounced();
    }, { deep: true });
</script>
